/**
 * Bundle 10 - Unified admin action audit feed.
 *
 * Route: /admin/audit (super_admin surfaced in the sidebar; endpoint is
 * admin+ readable so this component itself is gated at the router layer).
 *
 * Reads:
 *   GET /admin/audit
 *   GET /admin/audit/summary
 *   GET /admin/audit/export.csv (super_admin only)
 *
 * UX:
 *   - Filter bar (action_prefix, actor_email, target_type, from/to)
 *   - Table with relative + absolute timestamp, actor chip, colored
 *     category pill, action label, clickable target link (opens in new
 *     tab for user / registration), reason, expand row reveals pretty
 *     JSON meta + CopyJsonButton
 *   - Sticky "N in last 24h" chip at the top
 *   - Cursor "Load more" for older entries
 *   - Download CSV button (super_admin)
 *
 * Guardrails:
 *   - No em/en-dashes.
 *   - No eslint-disable directives anywhere.
 *   - Reuses existing color tokens (emerald / amber / rose / neutral / blue).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ChevronDown, ChevronRight, RefreshCw, Download, Filter as FilterIcon,
  ExternalLink, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { CopyJsonButton } from './CopyJsonButton';

const CATEGORY_STYLE = {
  impersonate: 'bg-amber-50 text-amber-800 border-amber-200',
  delete: 'bg-rose-50 text-rose-800 border-rose-200',
  edit: 'bg-blue-50 text-blue-800 border-blue-200',
  system: 'bg-neutral-50 text-neutral-600 border-neutral-200',
  maintenance: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  other: 'bg-neutral-50 text-neutral-500 border-neutral-200',
};

const TARGET_TYPE_OPTIONS = [
  { value: '__all__', label: 'All target types' },
  { value: 'user', label: 'user' },
  { value: 'registration', label: 'registration' },
  { value: 'tracking_pixel', label: 'tracking_pixel' },
  { value: 'tracking_pixels', label: 'tracking_pixels' },
  { value: 'pixel_event_logs', label: 'pixel_event_logs' },
  { value: 'emq_alert_config', label: 'emq_alert_config' },
  { value: 'zelle_gmail_credentials', label: 'zelle_gmail_credentials' },
  { value: 'progression_config', label: 'progression_config' },
  { value: 'legacy', label: 'legacy (target only)' },
];

const ACTION_PREFIX_OPTIONS = [
  { value: '__all__', label: 'All actions' },
  { value: 'admin.', label: 'admin.*' },
  { value: 'user.', label: 'user.*' },
  { value: 'registration.', label: 'registration.*' },
  { value: 'pixel.', label: 'pixel.*' },
  { value: 'pixel_analytics.', label: 'pixel_analytics.*' },
  { value: 'emq.', label: 'emq.*' },
  { value: 'zelle.', label: 'zelle.*' },
  { value: 'maintenance_run', label: 'maintenance_run:*' },
  { value: 'progression.', label: 'progression.*' },
  { value: 'achievement.', label: 'achievement.*' },
  { value: 'referral_', label: 'referral_*' },
];

const rel = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch (_) { return ''; }
};

const targetHref = (row) => {
  if (!row?.target_id) return null;
  if (row.target_type === 'user') return `/admin/users/${row.target_id}`;
  if (row.target_type === 'registration') return `/admin/registrations?id=${row.target_id}`;
  return null;
};

const AdminAuditPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [urlParams] = useSearchParams();
  const highRiskOnly = urlParams.get('high_risk') === '1' || urlParams.get('high_risk') === 'true';

  const [filters, setFilters] = useState({
    action_prefix: '__all__',
    actor_email: '',
    target_type: '__all__',
    from: '',
    to: '',
  });
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', '50');
    if (highRiskOnly) p.set('high_risk', 'true');
    if (filters.action_prefix && filters.action_prefix !== '__all__') p.set('action_prefix', filters.action_prefix);
    if (filters.actor_email) p.set('actor_email', filters.actor_email);
    if (filters.target_type && filters.target_type !== '__all__') p.set('target_type', filters.target_type);
    if (filters.from) p.set('from', filters.from);
    if (filters.to) p.set('to', filters.to);
    return p;
  }, [filters, highRiskOnly]);

  const load = useCallback(async ({ append = false } = {}) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams(queryParams);
      if (append && nextCursor) params.set('cursor', nextCursor);
      const { data } = await api.get(`/admin/audit?${params.toString()}`);
      setItems((prev) => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.next_cursor || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load audit feed.');
    } finally {
      setLoading(false);
    }
  }, [queryParams, nextCursor]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/audit/summary');
      setSummary(data);
    } catch (_) { /* silent */ }
  }, []);

  // Initial + on filter change. `load` and `loadSummary` are stable
  // via useCallback deps, but we intentionally trigger on filter changes
  // only so pagination cursor advances don't refetch the whole page.
  useEffect(() => {
    setItems([]); setNextCursor(null); setExpanded({});
    (async () => {
      setLoading(true); setError('');
      try {
        const params = new URLSearchParams(queryParams);
        const { data } = await api.get(`/admin/audit?${params.toString()}`);
        setItems(data.items || []);
        setNextCursor(data.next_cursor || null);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load audit feed.');
      } finally {
        setLoading(false);
      }
    })();
    loadSummary();
  }, [queryParams, loadSummary]);

  const onExportCsv = async () => {
    if (!isSuperAdmin) return;
    try {
      const params = new URLSearchParams(queryParams);
      params.delete('limit');
      const response = await api.get(`/admin/audit/export.csv?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin_audit_${new Date().toISOString().replace(/[:.]/g, '')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CSV export failed');
    }
  };

  return (
    <div className="p-6 max-w-6xl space-y-6" data-testid="admin-audit-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            {highRiskOnly ? 'Audit - High risk' : 'Audit'}
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            {highRiskOnly
              ? 'Only sensitive actions surfaced (deletes, role changes, impersonation, financial edits, destructive maintenance).'
              : 'Every sensitive admin action logged. Filter by action, actor, or target.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold"
              data-testid="admin-audit-24h-chip"
            >
              <Clock className="h-3.5 w-3.5" />
              {summary.total_24h} in last 24h
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setItems([]); setNextCursor(null); load({ append: false }); loadSummary(); }}
            data-testid="admin-audit-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {isSuperAdmin && (
            <Button
              size="sm"
              onClick={onExportCsv}
              className="bg-primary-800 hover:bg-primary-900 text-white"
              data-testid="admin-audit-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-neutral-500">
            <FilterIcon className="h-3.5 w-3.5" /> Filters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Action</Label>
              <Select
                value={filters.action_prefix}
                onValueChange={(v) => setFilters((s) => ({ ...s, action_prefix: v }))}
              >
                <SelectTrigger data-testid="admin-audit-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_PREFIX_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Target type</Label>
              <Select
                value={filters.target_type}
                onValueChange={(v) => setFilters((s) => ({ ...s, target_type: v }))}
              >
                <SelectTrigger data-testid="admin-audit-filter-target-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TARGET_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Actor email</Label>
              <Input
                value={filters.actor_email}
                onChange={(e) => setFilters((s) => ({ ...s, actor_email: e.target.value }))}
                placeholder="contains..."
                data-testid="admin-audit-filter-actor-email"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">From (ISO)</Label>
              <Input
                type="datetime-local"
                value={filters.from}
                onChange={(e) => setFilters((s) => ({ ...s, from: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                data-testid="admin-audit-filter-from"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">To (ISO)</Label>
              <Input
                type="datetime-local"
                value={filters.to}
                onChange={(e) => setFilters((s) => ({ ...s, to: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                data-testid="admin-audit-filter-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm" data-testid="admin-audit-error">
          {error}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-audit-table">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-neutral-500" data-testid="admin-audit-empty">
                      No audit entries match the current filters.
                    </td>
                  </tr>
                )}
                {items.map((row) => {
                  const isOpen = !!expanded[row.id];
                  const catStyle = CATEGORY_STYLE[row.category] || CATEGORY_STYLE.other;
                  const href = targetHref(row);
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className="border-b border-neutral-100 hover:bg-neutral-50/60"
                        data-testid={`admin-audit-row-${row.id}`}
                      >
                        <td className="px-3 py-2 align-top">
                          <button
                            type="button"
                            onClick={() => setExpanded((s) => ({ ...s, [row.id]: !isOpen }))}
                            className="text-neutral-500 hover:text-primary-900"
                            aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                            data-testid={`admin-audit-row-toggle-${row.id}`}
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-neutral-700 whitespace-nowrap">
                          <span title={row.created_at}>{rel(row.created_at)}</span>
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-neutral-800 whitespace-nowrap">
                          {row.actor_email || <span className="italic text-neutral-400">system</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${catStyle}`}
                              data-testid={`admin-audit-category-${row.id}`}
                            >
                              {row.category}
                            </span>
                            <code className="text-xs text-primary-900 font-mono">{row.action}</code>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-neutral-700">
                          {row.target_type ? (
                            <div className="flex items-center gap-1">
                              <span className="text-neutral-500">{row.target_type}:</span>
                              {href ? (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-secondary-700 hover:text-secondary-900 underline inline-flex items-center gap-0.5"
                                  data-testid={`admin-audit-target-link-${row.id}`}
                                >
                                  {row.target_id ? row.target_id.slice(0, 8) : '(none)'}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                <code className="font-mono text-neutral-800">{row.target_id ? row.target_id.slice(0, 8) : '-'}</code>
                              )}
                            </div>
                          ) : <span className="text-neutral-400">-</span>}
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-neutral-700 max-w-xs truncate" title={row.reason || ''}>
                          {row.reason || <span className="text-neutral-400">-</span>}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-neutral-100 bg-neutral-50/40">
                          <td></td>
                          <td colSpan={5} className="px-3 py-3">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Meta payload</div>
                              <CopyJsonButton
                                value={row.meta}
                                label="Copy meta"
                                testId={`admin-audit-copy-meta-${row.id}`}
                              />
                            </div>
                            <pre
                              className="text-[11px] bg-white border border-neutral-200 rounded-lg p-3 overflow-x-auto max-h-64"
                              data-testid={`admin-audit-meta-json-${row.id}`}
                            >{JSON.stringify(row.meta || {}, null, 2)}</pre>
                            <div className="mt-2 grid grid-cols-2 gap-3 text-[11px] text-neutral-500">
                              <div><span className="uppercase tracking-widest">Row id</span>: <code className="text-neutral-700">{row.id}</code></div>
                              <div><span className="uppercase tracking-widest">Actor id</span>: <code className="text-neutral-700">{row.actor_id || 'system'}</code></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-3 flex items-center justify-between border-t border-neutral-100">
            <div className="text-xs text-neutral-500">
              Showing {items.length} rows{nextCursor ? '' : ' (end of feed)'}
            </div>
            {nextCursor && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => load({ append: true })}
                disabled={loading}
                data-testid="admin-audit-load-more-btn"
              >
                Load more
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditPage;
